import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@1trading/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { CircleCheck, CircleX, Pencil } from 'lucide-react';
import { type InputHTMLAttributes, useEffect, useId, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { FieldError } from '@/components/ui/field-error';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { MARKETS_QUERY_KEY, usePerpOrderLimitsList, usePositionLimitsList } from '@/hooks/queries/markets/use-markets';
import { getErrorMessage } from '@/lib/api/error';
import {
  buildBulkMarketLimitsRequests,
  formatLimitAmount,
  indexPerpOrderLimitsBySymbol,
  indexPositionLimitsBySymbol,
  isLimitUnchanged,
  type Market,
  type MarketLimitsField,
  type MarketLimitsFormValues,
  POSITION_LIMIT_SEGMENT_LABELS,
  POSITION_LIMIT_SEGMENTS,
  type PositionLimit,
  type PositionLimitSegment,
  toMarketLimitsFormValues,
  updateMarketLimits,
} from '@/services/api/markets';
import { settleInSequence } from '@/utils/async';
import { buildMarketLimitsFieldsSchema, COMMENT_SCHEMA } from './market-limits-schema';

interface LimitColumn {
  field: MarketLimitsField;
  label: string;
  inputMode: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  isSegment: boolean;
}

const LIMIT_COLUMNS: readonly LimitColumn[] = [
  { field: 'priceCollarPercentage', label: 'Price Collar %', inputMode: 'decimal', isSegment: false },
  { field: 'maxOrderValue', label: 'Max Order Value', inputMode: 'numeric', isSegment: false },
  ...POSITION_LIMIT_SEGMENTS.map((segment) => ({
    field: segment,
    label: `${POSITION_LIMIT_SEGMENT_LABELS[segment]} Max Position`,
    inputMode: 'numeric' as const,
    isSegment: true,
  })),
];

const isSegmentField = (field: MarketLimitsField): field is PositionLimitSegment => (POSITION_LIMIT_SEGMENTS as readonly string[]).includes(field);

const pluralise = (count: number) => (count === 1 ? 'market' : 'markets');

type InitialRows = Readonly<Record<string, MarketLimitsFormValues>>;

const buildBulkFormSchema = (initialRows: InitialRows) =>
  z.object({
    rows: z.object(Object.fromEntries(Object.entries(initialRows).map(([symbol, initial]) => [symbol, buildMarketLimitsFieldsSchema(initial)]))),
    comment: COMMENT_SCHEMA,
  });

interface BulkFormValues {
  rows: Record<string, MarketLimitsFormValues>;
  comment: string;
}

interface SaveProgress {
  settled: number;
  total: number;
}

interface SaveFailure {
  symbol: string;
  message: string;
}

interface SaveOutcome {
  savedSymbols: string[];
  failures: SaveFailure[];
}

interface BulkMarketLimitsFormProps {
  markets: readonly Market[];
  initialRows: InitialRows;
  positionLimitsBySymbol: ReadonlyMap<string, PositionLimit>;
  progress: SaveProgress | null;
  onProgress: (progress: SaveProgress | null) => void;
  onSubmittingChange: (isSubmitting: boolean) => void;
  onPartialFailure: (outcome: SaveOutcome) => void;
  onClose: () => void;
}

const BulkMarketLimitsForm = ({ markets, initialRows, positionLimitsBySymbol, progress, onProgress, onSubmittingChange, onPartialFailure, onClose }: BulkMarketLimitsFormProps) => {
  const fieldId = useId();
  const queryClient = useQueryClient();
  const formSchema = useMemo(() => buildBulkFormSchema(initialRows), [initialRows]);
  const [fillValues, setFillValues] = useState<Partial<Record<MarketLimitsField, string>>>({});

  const form = useForm<BulkFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: { rows: initialRows, comment: '' },
  });
  const { errors, isSubmitting, isValid } = form.formState;
  useEffect(() => onSubmittingChange(isSubmitting), [isSubmitting, onSubmittingChange]);
  const rows = form.watch('rows');
  const comment = form.watch('comment');
  // Not memoised: watch() hands back the same mutated object, so a memo on it never recomputes
  const editedCount = buildBulkMarketLimitsRequests(rows, initialRows, '').length;

  const canEdit = (symbol: string, field: MarketLimitsField) => !isSegmentField(field) || positionLimitsBySymbol.has(symbol);

  // Clearing the fill box restores what each market opened with, rather than blanking (and so clearing) every row
  const handleFill = (field: MarketLimitsField, value: string) => {
    setFillValues((previous) => ({ ...previous, [field]: value }));
    for (const market of markets) {
      if (canEdit(market.id, field)) form.setValue(`rows.${market.id}.${field}`, value.trim() === '' ? initialRows[market.id][field] : value);
    }
    void form.trigger();
  };

  const rowErrors = markets.flatMap((market) =>
    LIMIT_COLUMNS.flatMap(({ field, label }) => {
      const message = errors.rows?.[market.id]?.[field]?.message;
      return message ? [{ key: `${market.id}-${field}`, text: `${market.id} · ${label}: ${message}` }] : [];
    }),
  );

  const handleSave = form.handleSubmit(async (values) => {
    const requests = buildBulkMarketLimitsRequests(values.rows, initialRows, values.comment);
    onProgress({ settled: 0, total: requests.length });
    // Each save rewrites bop-api's whole instruments record, so concurrent saves would clobber each other
    const results = await settleInSequence(requests, updateMarketLimits, (settled) => onProgress({ settled, total: requests.length }));
    onProgress(null);
    void queryClient.invalidateQueries({ queryKey: MARKETS_QUERY_KEY });

    const savedSymbols = results.filter((result) => result.status === 'fulfilled').map((result) => result.item.symbol);
    const failures = results.flatMap((result) => (result.status === 'rejected' ? [{ symbol: result.item.symbol, message: getErrorMessage(result.reason) }] : []));
    if (savedSymbols.length > 0) toast.success(`Updated limits for ${savedSymbols.length} ${pluralise(savedSymbols.length)}`);
    if (failures.length === 0) {
      onClose();
      return;
    }
    toast.error(`Failed to update limits for ${failures.length} ${pluralise(failures.length)}`);
    onPartialFailure({ savedSymbols, failures });
  });

  return (
    <form className="flex min-w-0 flex-col gap-5" noValidate onSubmit={handleSave}>
      <p className="text-xs text-muted-foreground">
        Each row opens with what the exchange enforces now. Type in the <span className="font-medium text-foreground">All selected</span> row to set a column for every market, then
        adjust single markets as needed. A blank segment uses the market default, shown greyed. Only edited markets are saved.
      </p>

      <div className="overflow-x-auto rounded-md border">
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow>
              <TableHead>Market</TableHead>
              {LIMIT_COLUMNS.map(({ field, label }) => (
                <TableHead className="whitespace-nowrap" key={field}>
                  {label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableCell className="font-medium whitespace-nowrap">All selected</TableCell>
              {LIMIT_COLUMNS.map(({ field, label, inputMode }) => (
                <TableCell key={field}>
                  <Input
                    aria-label={`${label} for all selected markets`}
                    className="h-8 w-32 tabular-nums"
                    disabled={isSubmitting}
                    inputMode={inputMode}
                    onChange={(event) => handleFill(field, event.target.value)}
                    placeholder="Set all"
                    type="text"
                    value={fillValues[field] ?? ''}
                  />
                </TableCell>
              ))}
            </TableRow>
            {markets.map((market) => {
              const positionLimit = positionLimitsBySymbol.get(market.id);
              return (
                <TableRow key={market.id}>
                  <TableCell className="whitespace-nowrap">
                    <div className="font-medium">{market.id}</div>
                    <div className="text-xs text-muted-foreground">in {market.quote.code}</div>
                  </TableCell>
                  {LIMIT_COLUMNS.map(({ field, label, inputMode, isSegment }) => {
                    if (!canEdit(market.id, field)) {
                      return (
                        <TableCell className="text-muted-foreground" key={field} title="The exchange reports no position limits for this market">
                          —
                        </TableCell>
                      );
                    }
                    const isEdited = !isLimitUnchanged(rows[market.id]?.[field] ?? '', initialRows[market.id][field]);
                    const hasError = Boolean(errors.rows?.[market.id]?.[field]);
                    return (
                      <TableCell key={field}>
                        <Input
                          aria-invalid={hasError}
                          aria-label={`${label} for ${market.id}`}
                          className={cn('h-8 w-32 tabular-nums', isEdited && !hasError && 'border-primary bg-primary/10')}
                          disabled={isSubmitting}
                          inputMode={inputMode}
                          placeholder={isSegment && positionLimit ? formatLimitAmount(positionLimit.baseMaxPos) : field === 'maxOrderValue' ? 'No cap' : undefined}
                          type="text"
                          {...form.register(`rows.${market.id}.${field}`)}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {rowErrors.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {rowErrors.map((error) => (
            <li key={error.key}>
              <FieldError message={error.text} />
            </li>
          ))}
        </ul>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${fieldId}-comment`}>Comment (required for audit trail)</Label>
        <Textarea disabled={isSubmitting} id={`${fieldId}-comment`} {...form.register('comment')} />
        <p className="text-xs text-muted-foreground">Recorded against every market saved. {comment.length} characters</p>
        <FieldError message={errors.comment?.message} />
      </div>

      <DialogFooter className="items-center gap-3 sm:justify-between">
        <p className="text-sm text-muted-foreground">{editedCount === 0 ? 'No changes yet' : `${editedCount} ${pluralise(editedCount)} changed`}</p>
        <div className="flex gap-2">
          <Button disabled={isSubmitting} onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button className="gap-2" disabled={editedCount === 0 || !isValid || isSubmitting} type="submit" variant="primary">
            {isSubmitting ? <Spinner className="size-4" /> : null}
            {progress
              ? `Saving ${Math.min(progress.settled + 1, progress.total)} of ${progress.total}`
              : editedCount === 0
                ? 'Save'
                : `Save ${editedCount} ${pluralise(editedCount)}`}
          </Button>
        </div>
      </DialogFooter>
    </form>
  );
};
BulkMarketLimitsForm.displayName = 'BulkMarketLimitsForm';

interface SaveOutcomeSummaryProps {
  outcome: SaveOutcome;
  onClose: () => void;
}

const SaveOutcomeSummary = ({ outcome, onClose }: SaveOutcomeSummaryProps) => (
  <div className="flex flex-col gap-4">
    <ul className="flex flex-col gap-2">
      {outcome.failures.map((failure) => (
        <li className="flex items-start gap-2 text-sm" key={failure.symbol}>
          <CircleX aria-hidden className="mt-0.5 size-4 shrink-0 text-red-500" />
          <span>
            <span className="font-medium">{failure.symbol}</span> <span className="text-muted-foreground">{failure.message}</span>
          </span>
        </li>
      ))}
      {outcome.savedSymbols.map((symbol) => (
        <li className="flex items-center gap-2 text-sm" key={symbol}>
          <CircleCheck aria-hidden className="size-4 shrink-0 text-emerald-500" />
          <span className="font-medium">{symbol}</span>
        </li>
      ))}
    </ul>
    <p className="text-xs text-muted-foreground">A failed market may have had some of its limits applied. Reopen the editor to see what the exchange now enforces and retry.</p>
    <DialogFooter>
      <Button onClick={onClose} type="button" variant="primary">
        Close
      </Button>
    </DialogFooter>
  </div>
);
SaveOutcomeSummary.displayName = 'SaveOutcomeSummary';

interface BulkMarketLimitsDialogProps {
  /** Futures markets only */
  markets: readonly Market[];
  /** Selected markets left out because limits do not apply to them */
  skippedCount: number;
  onClose: () => void;
}

/** Edit the limits of many futures markets in one grid, saved under one comment. */
export const BulkMarketLimitsDialog = ({ markets, skippedCount, onClose }: BulkMarketLimitsDialogProps) => {
  const orderLimitsQuery = usePerpOrderLimitsList();
  const positionLimitsQuery = usePositionLimitsList();
  const [isRefreshing, setIsRefreshing] = useState(true);
  const [progress, setProgress] = useState<SaveProgress | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasStaleBaseline, setHasStaleBaseline] = useState(false);
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);

  // The table's cached limits may be stale; the grid's baseline must be what the exchange enforces now
  // biome-ignore lint/correctness/useExhaustiveDependencies: refetch once per open
  useEffect(() => {
    void Promise.all([orderLimitsQuery.refetch(), positionLimitsQuery.refetch()]).then((results) => {
      setHasStaleBaseline(results.some((result) => result.isError && result.data !== undefined));
      setIsRefreshing(false);
    });
  }, []);

  const positionLimitsBySymbol = useMemo(() => indexPositionLimitsBySymbol(positionLimitsQuery.data ?? []), [positionLimitsQuery.data]);
  const [initialRows, setInitialRows] = useState<InitialRows | null>(null);
  useEffect(() => {
    if (isRefreshing || initialRows) return;
    const orderLimitsBySymbol = indexPerpOrderLimitsBySymbol(orderLimitsQuery.data ?? []);
    setInitialRows(
      Object.fromEntries(markets.map((market) => [market.id, toMarketLimitsFormValues(orderLimitsBySymbol.get(market.id) ?? null, positionLimitsBySymbol.get(market.id))])),
    );
  }, [isRefreshing, initialRows, markets, orderLimitsQuery.data, positionLimitsBySymbol]);

  const loadFailures = [
    orderLimitsQuery.isError &&
      orderLimitsQuery.data === undefined &&
      'The current order limits could not be loaded: those fields start blank and are only sent if you type a value.',
    positionLimitsQuery.isError && positionLimitsQuery.data === undefined && 'The position limits could not be loaded, so they cannot be set here.',
    hasStaleBaseline && 'The latest limits could not be loaded, so some rows may show older values. A value typed to match an old one is not sent: reopen to retry.',
  ].filter(Boolean);

  return (
    <Dialog onOpenChange={(open) => (open || isSubmitting ? undefined : onClose())} open>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="size-5" />
            {outcome ? 'Some limits were not saved' : `Edit Limits - ${markets.length} ${pluralise(markets.length)}`}
          </DialogTitle>
        </DialogHeader>

        {skippedCount > 0 && !outcome ? (
          <p className="text-xs text-muted-foreground">
            {skippedCount} selected spot {pluralise(skippedCount)} skipped: limits only apply to futures.
          </p>
        ) : null}
        {loadFailures.map((message) => (
          <p className="text-xs text-amber-600 dark:text-amber-500" key={String(message)}>
            {message}
          </p>
        ))}

        {outcome ? (
          <SaveOutcomeSummary onClose={onClose} outcome={outcome} />
        ) : initialRows ? (
          <BulkMarketLimitsForm
            initialRows={initialRows}
            markets={markets}
            onClose={onClose}
            onPartialFailure={setOutcome}
            onProgress={setProgress}
            onSubmittingChange={setIsSubmitting}
            positionLimitsBySymbol={positionLimitsBySymbol}
            progress={progress}
          />
        ) : (
          <LoadingPanel className="py-10" label="Loading limits" />
        )}
      </DialogContent>
    </Dialog>
  );
};
BulkMarketLimitsDialog.displayName = 'BulkMarketLimitsDialog';
